import React, { useState, useEffect, useMemo, useRef } from 'react';
import axios from 'axios';
import {
  ChevronRight,
  ChevronDown,
  AlertTriangle,
  Wrench,
  Shield,
  Lightbulb,
  MessageSquare,
  Plus,
  Info,
  Check,
  X,
  RefreshCw,
  Zap,
  Link2,
  FileText
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { API } from '@/contexts/AuthContext';
import { toast } from 'sonner';

/**
 * JCEV2Panel - Repair Intent Based Job Completeness Engine
 * 
 * This is the new JCE architecture based on canonical repair intents.
 * It provides:
 * - Disturbances: Components likely affected by the repair
 * - Suggested Additions: Items to consider adding to the estimate
 * - Risks: Common repair considerations
 * - FOH Guidance: Pricing tips for FOH
 * - Customer Explanation: How to explain to the customer
 */

// Repair intent mapping (auto-detect from VI item keys)
// This maps VI inspection item keys to JCE repair intent codes
const REPAIR_INTENT_MAPPING = {
  // === BRAKES ===
  "front_brake_pads": "BRAKE_FRONT_OVERHAUL",
  "front_brake_discs": "BRAKE_FRONT_OVERHAUL",
  "rear_brake_pads": "BRAKE_REAR_OVERHAUL",
  "rear_brake_discs": "BRAKE_REAR_OVERHAUL",
  "handbrake_shoes": "BRAKE_REAR_OVERHAUL",
  "brake_hoses_lines": "BRAKE_FRONT_OVERHAUL", // Could be front or rear - default to front
  "parking_brake_operation": "BRAKE_REAR_OVERHAUL",
  
  // === BRAKE FLUID ===
  "brake_fluid": "BRAKE_FLUID_SERVICE",
  "brake_fluid_condition": "BRAKE_FLUID_SERVICE",
  "brake_fluid_boiling_point": "BRAKE_FLUID_SERVICE",
  "brake_fluid_boiling_point_test": "BRAKE_FLUID_SERVICE",
  
  // === SUSPENSION - FRONT ===
  "front_springs": "SUSPENSION_FRONT_SPRING_REPLACEMENT",
  "front_springs_ns": "SUSPENSION_FRONT_SPRING_REPLACEMENT",
  "front_springs_os": "SUSPENSION_FRONT_SPRING_REPLACEMENT",
  "front_shock_absorbers": "SUSPENSION_FRONT_SHOCK_REPLACEMENT",
  "front_shock_absorbers_ns": "SUSPENSION_FRONT_SHOCK_REPLACEMENT",
  "front_shock_absorbers_os": "SUSPENSION_FRONT_SHOCK_REPLACEMENT",
  "front_top_mounts": "SUSPENSION_FRONT_TOP_MOUNT_REPLACEMENT",
  "front_lower_arm": "SUSPENSION_FRONT_ARM_REPLACEMENT",
  // Suspension arm canonical VI keys (MOT auto-quote bridge).
  "front_suspension_arms":    "SUSPENSION_FRONT_ARM_REPLACEMENT",
  "front_suspension_arms_ns": "SUSPENSION_FRONT_ARM_REPLACEMENT",
  "front_suspension_arms_os": "SUSPENSION_FRONT_ARM_REPLACEMENT",
  "front_suspension_arm":     "SUSPENSION_FRONT_ARM_REPLACEMENT",
  "front_suspension_arm_ns":  "SUSPENSION_FRONT_ARM_REPLACEMENT",
  "front_suspension_arm_os":  "SUSPENSION_FRONT_ARM_REPLACEMENT",
  "front_wishbone":           "SUSPENSION_FRONT_ARM_REPLACEMENT",
  "front_control_arm":        "SUSPENSION_FRONT_ARM_REPLACEMENT",
  "front_drop_link": "SUSPENSION_FRONT_DROP_LINK_REPLACEMENT",
  "front_track_rod_end": "SUSPENSION_FRONT_TRACK_ROD_REPLACEMENT",
  "track_rod_ends": "SUSPENSION_FRONT_TRACK_ROD_REPLACEMENT",
  "front_wheel_bearing": "SUSPENSION_FRONT_WHEEL_BEARING_REPLACEMENT",
  "anti_roll_bar_links": "SUSPENSION_FRONT_DROP_LINK_REPLACEMENT",
  "ball_joints": "SUSPENSION_FRONT_ARM_REPLACEMENT",
  "steering_rack": "SUSPENSION_FRONT_TRACK_ROD_REPLACEMENT",
  
  // === SUSPENSION - REAR ===
  "rear_springs": "SUSPENSION_REAR_SPRING_REPLACEMENT",
  "rear_shock_absorbers": "SUSPENSION_REAR_SHOCK_REPLACEMENT",
  "rear_wheel_bearing": "SUSPENSION_REAR_WHEEL_BEARING_REPLACEMENT",
  // Rear suspension arm canonical VI keys → dedicated rear-arm intent.
  "rear_suspension_arms":    "SUSPENSION_REAR_ARM_REPLACEMENT",
  "rear_suspension_arms_ns": "SUSPENSION_REAR_ARM_REPLACEMENT",
  "rear_suspension_arms_os": "SUSPENSION_REAR_ARM_REPLACEMENT",
  "rear_suspension_arm":     "SUSPENSION_REAR_ARM_REPLACEMENT",
  "rear_trailing_arm":       "SUSPENSION_REAR_ARM_REPLACEMENT",
  "rear_control_arm":        "SUSPENSION_REAR_ARM_REPLACEMENT",
  "rear_anti_roll_bar_links": "SUSPENSION_REAR_SHOCK_REPLACEMENT",
  "subframe_rear": "SUBFRAME_REAR_REPLACEMENT",
  
  // === WHEEL BEARINGS ===
  "wheel_bearings": "SUSPENSION_FRONT_WHEEL_BEARING_REPLACEMENT",
  
  // === DRIVETRAIN ===
  "clutch": "CLUTCH_SYSTEM_REPLACEMENT",
  "cv_joints_boots": "CV_BOOT_REPLACEMENT",
  "driveshaft": "DRIVESHAFT_REPLACEMENT",
  
  // === TIMING ===
  "timing_belt": "TIMING_BELT_JOB",
  "timing_chain": "TIMING_CHAIN_JOB",
  
  // === DRIVE BELTS ===
  "drive_belts": "DRIVE_BELT_REPLACEMENT",
  "v_belt": "DRIVE_BELT_REPLACEMENT",
  "v_belts": "DRIVE_BELT_REPLACEMENT",
  "v-belt": "DRIVE_BELT_REPLACEMENT",
  "aux_belt": "DRIVE_BELT_REPLACEMENT",
  "auxiliary_belt": "DRIVE_BELT_REPLACEMENT",
  "serpentine_belt": "DRIVE_BELT_REPLACEMENT",
  
  // === ELECTRICAL ===
  "battery": "BATTERY_REPLACEMENT",
  "battery_test": "BATTERY_REPLACEMENT",
  "alternator": "ALTERNATOR_REPLACEMENT",
  "starter_motor": "STARTER_MOTOR_REPLACEMENT",
  "dash_warning_lamps": "DIAGNOSTIC_INVESTIGATION",
  "warning_lights": "DIAGNOSTIC_INVESTIGATION",
  
  // === TYRES ===
  "tyre": "TYRE_REPLACEMENT",
  "tyre_fl": "TYRE_REPLACEMENT",
  "tyre_fr": "TYRE_REPLACEMENT",
  "tyre_rl": "TYRE_REPLACEMENT",
  "tyre_rr": "TYRE_REPLACEMENT",
  "tyre_front_left": "TYRE_REPLACEMENT",
  "tyre_front_right": "TYRE_REPLACEMENT",
  "tyre_rear_left": "TYRE_REPLACEMENT",
  "tyre_rear_right": "TYRE_REPLACEMENT",
  "front_tyre": "TYRE_REPLACEMENT",
  "rear_tyre": "TYRE_REPLACEMENT",
  "spare_tyre": "TYRE_REPLACEMENT",
  
  // === COOLANT ===
  "coolant": "COOLANT_SERVICE",
  "coolant_level": "COOLANT_SERVICE",
  "coolant_condition": "COOLANT_SERVICE",
  "coolant_freeze_point_test": "COOLANT_SERVICE",
  "antifreeze": "COOLANT_SERVICE",
  "hoses_connections": "COOLANT_SERVICE",
  
  // === ENGINE OIL ===
  "engine_oil_level_condition": "ENGINE_OIL_SERVICE",
  "engine_transmission_leaks": "ENGINE_OIL_LEAK_REPAIR",
  
  // === EXHAUST ===
  "exhaust_system": "EXHAUST_REPAIR",
  "catalytic_converter": "CATALYTIC_CONVERTER_REPLACEMENT",
  "dpf_condition": "DPF_SERVICE",
  
  // === AIR CON ===
  "air_con": "AIRCON_SERVICE",
  "aircon": "AIRCON_SERVICE",
  "aircon_operation": "AIRCON_SERVICE",
  "ac_system": "AIRCON_SERVICE",
  
  // === HEATER ===
  "heater_fan_controls": "HEATER_SERVICE",
  
  // === POWER STEERING ===
  "power_steering_fluid": "COOLANT_SERVICE", // Uses similar service approach
  
  // === FUEL SYSTEM ===
  "fuel_lines_tank": "DIAGNOSTIC_INVESTIGATION",
  "adblue_level": "DIAGNOSTIC_INVESTIGATION",
  
  // === STRUCTURAL ===
  "underside_corrosion": "WELDING_STRUCTURAL",

  // === WHEEL ALIGNMENT (primary job) ===
  // Mirror the backend mapping (services/jce_v2/repair_intents.py).
  // The alignment_engine sets vi_item_key="wheel_alignment" on the
  // recommendation job-group, plus we accept every common phrasing
  // FOH might type or import from MOT advisories.
  "wheel_alignment":        "WHEEL_ALIGNMENT_SERVICE",
  "wheel_alignment_check":  "WHEEL_ALIGNMENT_SERVICE",
  "four_wheel_alignment":   "WHEEL_ALIGNMENT_SERVICE",
  "4_wheel_alignment":      "WHEEL_ALIGNMENT_SERVICE",
  "front_wheel_alignment":  "WHEEL_ALIGNMENT_SERVICE",
  "thrust_alignment":       "WHEEL_ALIGNMENT_SERVICE",
  "alignment_check":        "WHEEL_ALIGNMENT_SERVICE",
  "geometry_check":         "WHEEL_ALIGNMENT_SERVICE",
  "tracking":               "WHEEL_ALIGNMENT_SERVICE",
  "tracking_adjustment":    "WHEEL_ALIGNMENT_SERVICE",
  "toe_adjustment":         "WHEEL_ALIGNMENT_SERVICE",
  "camber_adjustment":      "WHEEL_ALIGNMENT_SERVICE",
  "uneven_tyre_wear":       "WHEEL_ALIGNMENT_SERVICE",
  "vehicle_pulling":        "WHEEL_ALIGNMENT_SERVICE",
  "steering_off_centre":    "WHEEL_ALIGNMENT_SERVICE"
};

// Get repair intent from VI item key
const getRepairIntent = (viItemKey) => {
  if (!viItemKey) return null;
  const key = viItemKey.toLowerCase();
  
  // Direct match
  if (REPAIR_INTENT_MAPPING[key]) return REPAIR_INTENT_MAPPING[key];
  
  // Partial match
  for (const [pattern, intent] of Object.entries(REPAIR_INTENT_MAPPING)) {
    if (key.includes(pattern) || pattern.includes(key)) {
      return intent;
    }
  }
  return null;
};

// iter197g — MCC / canonical-rule / JCU-package → repair intent maps.
// Mirrors the maps in JCEV2InlineHints. Required to surface JCE
// guidance on MCC-driven and DVSA-imported job groups (e.g.
// shock_absorber on WX12FYD whose vi_item_key is null).
const MCC_CATEGORY_TO_INTENT = {
  tyre:            "TYRE_REPLACEMENT",
  brake:           "BRAKE_FRONT_OVERHAUL",
  suspension_arm:  "SUSPENSION_FRONT_ARM_REPLACEMENT",
  ball_joint:      "SUSPENSION_FRONT_ARM_REPLACEMENT",
  shock_absorber:  "SUSPENSION_FRONT_SHOCK_REPLACEMENT",
  coil_spring:     "SUSPENSION_FRONT_SPRING_REPLACEMENT",
  wheel_bearing:   "SUSPENSION_FRONT_WHEEL_BEARING_REPLACEMENT",
  battery:         "BATTERY_REPLACEMENT",
  fluid_leak:      "ENGINE_OIL_LEAK_REPAIR",
  // iter198 — wheel alignment as a primary recommendation.
  wheel_alignment: "WHEEL_ALIGNMENT_SERVICE",
  alignment:       "WHEEL_ALIGNMENT_SERVICE",
};
const CANONICAL_RULE_TO_INTENT = {
  rule_front_shocks:       "SUSPENSION_FRONT_SHOCK_REPLACEMENT",
  rule_rear_shocks:        "SUSPENSION_REAR_SHOCK_REPLACEMENT",
  rule_front_springs:      "SUSPENSION_FRONT_SPRING_REPLACEMENT",
  rule_rear_springs:       "SUSPENSION_FRONT_SPRING_REPLACEMENT",
  rule_front_top_mounts:   "SUSPENSION_FRONT_TOP_MOUNT_REPLACEMENT",
  rule_front_lower_arm:    "SUSPENSION_FRONT_ARM_REPLACEMENT",
  rule_ball_joints:        "SUSPENSION_FRONT_ARM_REPLACEMENT",
  rule_track_rod_ends:     "SUSPENSION_FRONT_TRACK_ROD_REPLACEMENT",
  rule_anti_roll_bar_links:"SUSPENSION_FRONT_DROP_LINK_REPLACEMENT",
  rule_wheel_bearings:     "SUSPENSION_FRONT_WHEEL_BEARING_REPLACEMENT",
  rule_tyre_replacement:   "TYRE_REPLACEMENT",
  // iter198 — was "WHEEL_ALIGNMENT_4WHEEL" which doesn't exist in
  // the backend seed; the alignment recommendation now routes to
  // the canonical JCE_WHEEL_ALIGNMENT rule via WHEEL_ALIGNMENT_SERVICE.
  rule_wheel_alignment:    "WHEEL_ALIGNMENT_SERVICE",
};
const JCU_PACKAGE_TO_INTENT = {
  pkg_front_shocks:           "SUSPENSION_FRONT_SHOCK_REPLACEMENT",
  pkg_rear_shocks:            "SUSPENSION_REAR_SHOCK_REPLACEMENT",
  pkg_front_springs:          "SUSPENSION_FRONT_SPRING_REPLACEMENT",
  pkg_rear_springs:           "SUSPENSION_FRONT_SPRING_REPLACEMENT",
  pkg_top_mount:              "SUSPENSION_FRONT_TOP_MOUNT_REPLACEMENT",
  pkg_lower_arm:              "SUSPENSION_FRONT_ARM_REPLACEMENT",
  pkg_ball_joint:             "SUSPENSION_FRONT_ARM_REPLACEMENT",
  pkg_track_rod_end:          "SUSPENSION_FRONT_TRACK_ROD_REPLACEMENT",
  pkg_arb_links:              "SUSPENSION_FRONT_DROP_LINK_REPLACEMENT",
  pkg_wheel_bearing:          "SUSPENSION_FRONT_WHEEL_BEARING_REPLACEMENT",
  pkg_front_brake_discs_pads: "BRAKE_FRONT_OVERHAUL",
  pkg_rear_brake_discs_pads:  "BRAKE_REAR_OVERHAUL",
};

// iter199ab — Title-substring + source-based fallback. Mirrors the
// backend extract_repair_intents_from_yestimate helper. Required for
// TSP-generated JGs (Timing Belt & Water Pump Service, Wet Belt
// Service, Timing Chain Service) whose vi_item_key / mcc_category /
// canonical_rule_id are all null. Without this, JCE rendered
// "0 rules matched" until the next page refresh AFTER a save (and even
// then only when the backend preserved repair_intent_code).
const SOURCE_TO_INTENT_FALLBACK = {
  timing_service_pack: "TIMING_BELT_JOB",
};
const TITLE_TO_INTENT_FALLBACK = [
  // [substring (lower-cased), intent]. Order matters — more specific first.
  [/wet\s*belt/i,              "TIMING_BELT_JOB"],
  [/timing\s*belt/i,           "TIMING_BELT_JOB"],
  [/timing\s*chain/i,          "TIMING_CHAIN_JOB"],
  [/cam[\s-]*belt/i,           "TIMING_BELT_JOB"],
  [/water\s*pump/i,            "WATER_PUMP_REPLACEMENT"],
  [/auxiliary\s*belt/i,        "DRIVE_BELT_REPLACEMENT"],
  [/aux\s*belt/i,              "DRIVE_BELT_REPLACEMENT"],
  [/serpentine\s*belt/i,       "DRIVE_BELT_REPLACEMENT"],
  [/drive\s*belt/i,            "DRIVE_BELT_REPLACEMENT"],
  [/wheel\s*alignment/i,       "WHEEL_ALIGNMENT_SERVICE"],
  [/four\s*wheel\s*align/i,    "WHEEL_ALIGNMENT_SERVICE"],
];

// Resolve an intent code for a JG by trying every available identifier.
// Used by both JCEV2Panel and JCEV2InlineHints so MCC-derived JGs render
// the same panel as VI-derived JGs.
const resolveIntentForJob = (job) => {
  if (!job) return null;
  const fromVi = getRepairIntent(job.vi_item_key);
  if (fromVi) return fromVi;
  if (job.repair_intent_code) return job.repair_intent_code;
  if (job.mcc_category && MCC_CATEGORY_TO_INTENT[job.mcc_category]) return MCC_CATEGORY_TO_INTENT[job.mcc_category];
  if (job.canonical_rule_id && CANONICAL_RULE_TO_INTENT[job.canonical_rule_id]) return CANONICAL_RULE_TO_INTENT[job.canonical_rule_id];
  if (job.jcu_package_id && JCU_PACKAGE_TO_INTENT[job.jcu_package_id]) return JCU_PACKAGE_TO_INTENT[job.jcu_package_id];
  // iter199ab — Source / title fallback (TSP packs etc).
  const src = (job.source || "").trim().toLowerCase();
  if (src && SOURCE_TO_INTENT_FALLBACK[src]) return SOURCE_TO_INTENT_FALLBACK[src];
  const title = (job.title || "").trim();
  if (title) {
    for (const [pattern, intent] of TITLE_TO_INTENT_FALLBACK) {
      if (pattern.test(title)) return intent;
    }
  }
  return null;
};

// Confidence badge colors
const getConfidenceBadge = (confidence) => {
  switch (confidence) {
    case 'high':
      return <Badge className="bg-red-100 text-red-700 text-xs">High</Badge>;
    case 'medium':
      return <Badge className="bg-amber-100 text-amber-700 text-xs">Medium</Badge>;
    case 'low':
      return <Badge className="bg-slate-100 text-slate-600 text-xs">Low</Badge>;
    default:
      return null;
  }
};

const JCEV2Panel = ({
  isOpen,
  onClose,
  jobGroups = [],
  selectedJob = null,
  vehicleContext = {},
  onAddSuggestion,
  onSelectJob,
  viItems = []  // VI items for smart filtering
}) => {
  const [jceData, setJceData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [serviceMappingLoading, setServiceMappingLoading] = useState(false); // Separate loading for service mapping
  const [error, setError] = useState(null);
  const [serviceMapping, setServiceMapping] = useState(null); // For service interval items
  const [addedSuggestionCodes, setAddedSuggestionCodes] = useState(new Set()); // Track explicitly added suggestions
  const [expandedSections, setExpandedSections] = useState({
    disturbances: true,
    suggestions: true,
    risks: true,
    fohGuidance: true,
    customerExplanation: false
  });

  // Check if selected job is a service interval item
  // BUT if it has a specific repair intent mapping, treat it as a regular repair
  const hasSpecificRepairIntent = !!resolveIntentForJob(selectedJob);
  const isServiceIntervalItem = (selectedJob?.is_interval_item || selectedJob?.source === 'interval') && !hasSpecificRepairIntent;

  // Extract repair intents - JOB-SPECIFIC when a job is selected
  const repairIntents = useMemo(() => {
    const intents = new Set();
    
    // If it's a service interval item, don't extract repair intents (use service mapping instead)
    if (isServiceIntervalItem) {
      return [];
    }
    
    // If a specific job is selected, only get intents for that job.
    // iter197g — resolveIntentForJob also walks mcc_category /
    // canonical_rule_id / jcu_package_id so MCC-derived JGs surface
    // the JCE panel just like VI-derived ones.
    if (selectedJob) {
      const resolved = resolveIntentForJob(selectedJob);
      if (resolved) intents.add(resolved);
      return Array.from(intents);
    }
    
    // No job selected - return empty (will show neutral state)
    return [];
  }, [selectedJob, isServiceIntervalItem]);
  
  // Get the selected job's title for display
  const selectedJobTitle = selectedJob?.title || null;
  
  // Reset tracked suggestion codes when switching between jobs
  const selectedJobId = selectedJob?.id;
  useEffect(() => {
    setAddedSuggestionCodes(new Set());
  }, [selectedJobId]);
  
  // Filter out suggestions that have already been added to the job
  // Uses STRICT CODE-ONLY matching to prevent false positives where related but
  // distinct items (e.g. "Belt Tensioner" vs "Idler Pulley") incorrectly disappear.
  // Also filters out brake fluid suggestion if VI brake fluid test is green/blue.
  //
  // iter198mm — Description-fallback dedup. When a JG is auto-populated from a
  // pricing template (e.g. front_brake_discs), the inserted parts carry a
  // description like "Brake Pad Fitting Kit" but NO `jce_code`. The code-only
  // dedup therefore failed to hide the JCE V2 "Consider Adding" suggestion,
  // and one click duplicated the line in the pricing panel. We now also dedup
  // by a small allow-list of known equivalence keys mapped to description
  // substrings. Conservative — only matches items we know are 1:1 (fitting
  // kit, brake fluid, copper grease, brake cleaner) so unrelated suggestions
  // like "Top Mount" vs "Strut Mount" are NOT collapsed.
  const filteredJceData = useMemo(() => {
    if (!jceData) return null;
    
    // Get existing parts and labour from the selected job
    const existingParts = selectedJob?.parts_lines || [];
    const existingLabour = selectedJob?.labour_lines || [];
    
    // Build a set of JCE codes already present in the job lines
    const existingJceCodes = new Set();
    existingParts.forEach(p => {
      if (p.jce_code) existingJceCodes.add(p.jce_code.toUpperCase());
    });
    existingLabour.forEach(l => {
      if (l.jce_code) existingJceCodes.add(l.jce_code.toUpperCase());
    });

    // iter198mm — Description-fallback equivalence map.
    // Keyed by JCE V2 suggestion code; value is a substring matcher run
    // against existing parts/labour DESCRIPTIONS. Match is case-insensitive
    // and treats hyphens / extra qualifiers (e.g. "- Front") as noise.
    const DESCRIPTION_EQUIVALENCE = {
      BRAKE_FITTING_KIT:      (d) => /fitting\s*kit/i.test(d),
      BRAKE_FITTING_KIT_REAR: (d) => /fitting\s*kit/i.test(d),
      BRAKE_FLUID:            (d) => /brake\s*fluid/i.test(d),
      BRAKE_FLUID_CHECK:      (d) => /brake\s*fluid/i.test(d),
      COPPER_GREASE:          (d) => /copper\s*grease/i.test(d),
      BRAKE_CLEANER:          (d) => /brake\s*cleaner/i.test(d),
    };
    const existingDescriptions = [
      ...existingParts.map(p => p.description || ''),
      ...existingLabour.map(l => l.description || ''),
    ].filter(Boolean);
    
    // Check VI status for brake fluid
    const brakeFluidVIItem = viItems?.find(i => 
      i.item_key === 'brake_fluid_test' || 
      i.item_key === 'brake_fluid_condition' ||
      i.item_key === 'brake_fluid'
    );
    const brakeFluidNeedsAttention = brakeFluidVIItem && 
      (brakeFluidVIItem.status === 'red' || brakeFluidVIItem.status === 'amber');
    
    // Filter out suggestions that should NOT be shown
    const filteredSuggestions = jceData.suggested_additions.filter(s => {
      const codeUpper = s.code?.toUpperCase().trim();
      const codeLower = s.code?.toLowerCase().trim();
      const labelLower = s.label?.toLowerCase().trim();
      
      // CODE-ONLY CHECK: Exact code match against parts_lines/labour_lines jce_code
      if (codeUpper && existingJceCodes.has(codeUpper)) {
        return false;
      }
      
      // Also check the locally-tracked set (covers instant UI feedback before
      // the parent re-renders with updated parts_lines)
      if (codeUpper && addedSuggestionCodes.has(codeUpper)) {
        return false;
      }

      // iter198mm — Description-fallback dedup. Catches the case where the
      // pricing template populated a part with the same purpose but no
      // jce_code (so the CODE-ONLY check above misses it).
      const matcher = codeUpper && DESCRIPTION_EQUIVALENCE[codeUpper];
      if (matcher && existingDescriptions.some(matcher)) {
        return false;
      }
      
      // Brake fluid business rule: hide if VI says fluid is OK (green/blue)
      const isBrakeFluidSuggestion = /brake.*fluid|brake_fluid/i.test(codeLower || '') || 
                                     /brake.*fluid/i.test(labelLower || '');
      if (isBrakeFluidSuggestion && !brakeFluidNeedsAttention) {
        return false;
      }
      
      return true;
    });
    
    return {
      ...jceData,
      suggested_additions: filteredSuggestions
    };
  }, [jceData, selectedJob?.parts_lines, selectedJob?.labour_lines, viItems, addedSuggestionCodes]);

  // Fetch service mapping for service interval items
  useEffect(() => {
    console.log('JCEV2Panel useEffect - isServiceIntervalItem:', isServiceIntervalItem, 'selectedJob:', selectedJob?.id, selectedJob?.is_interval_item, selectedJob?.source);
    
    if (!isServiceIntervalItem || !selectedJob) {
      setServiceMapping(null);
      return;
    }

    const fetchServiceMapping = async () => {
      setServiceMappingLoading(true);
      try {
        // Use interval_item_id if available, otherwise fall back to vi_item_key for interval items
        // This handles cases where interval_item_id wasn't saved properly
        const serviceItemId = selectedJob.interval_item_id || selectedJob.vi_item_key || 'oil_filter_service';
        // Use BACKEND_URL directly since API already has /api prefix
        const backendUrl = process.env.REACT_APP_BACKEND_URL;
        console.log('JCEV2Panel - Fetching service mapping for:', serviceItemId, 'from', `${backendUrl}/api/service-mappings/${serviceItemId}`);
        const response = await axios.get(`${backendUrl}/api/service-mappings/${serviceItemId}`, {
          params: {
            vehicle_make: vehicleContext?.make,
            vehicle_model: vehicleContext?.model,
            fuel_type: vehicleContext?.fuel_type,
            year: vehicleContext?.year
          }
        });
        console.log('JCEV2Panel - Service mapping response:', response.data);
        setServiceMapping(response.data);
        setJceData(null); // Clear JCE data when showing service mapping
      } catch (err) {
        console.error('Failed to fetch service mapping:', err);
        setServiceMapping(null);
      } finally {
        setServiceMappingLoading(false);
      }
    };

    fetchServiceMapping();
  }, [selectedJob, isServiceIntervalItem, vehicleContext]);

  // Ref for debouncing JCE analysis fetch
  const jceFetchTimeoutRef = useRef(null);
  const lastFetchParamsRef = useRef(null);

  // Fetch JCE analysis when intents change (with debouncing to prevent flickering)
  useEffect(() => {
    if (repairIntents.length === 0) {
      setJceData(null);
      return;
    }

    // Create a stable key from current params to compare with last fetch
    const currentParamsKey = JSON.stringify({
      intents: repairIntents.sort(),
      make: vehicleContext?.make,
      model: vehicleContext?.model,
      fuel_type: vehicleContext?.fuel_type,
      year: vehicleContext?.year
    });

    // Skip if params haven't actually changed (prevents unnecessary flicker)
    if (lastFetchParamsRef.current === currentParamsKey) {
      return;
    }

    // Clear any pending fetch
    if (jceFetchTimeoutRef.current) {
      clearTimeout(jceFetchTimeoutRef.current);
    }

    // Debounce the fetch to prevent rapid re-fetching
    jceFetchTimeoutRef.current = setTimeout(async () => {
      // Double-check params haven't changed during debounce
      const recheckParamsKey = JSON.stringify({
        intents: repairIntents.sort(),
        make: vehicleContext?.make,
        model: vehicleContext?.model,
        fuel_type: vehicleContext?.fuel_type,
        year: vehicleContext?.year
      });
      
      if (lastFetchParamsRef.current === recheckParamsKey) {
        return; // Params changed and match what we already have
      }
      
      setLoading(true);
      setError(null);
      try {
        // Iter188 — strip empty strings from vehicle_context before
        // POSTing so a JG on an "Unknown Unknown" vehicle (DK11TKN)
        // doesn't trigger Pydantic's int_parsing 422 on year/mileage.
        // The backend's VehicleContext now also coerces these
        // server-side, but stripping client-side keeps the wire
        // payload clean and avoids a "Failed to load JCE guidance"
        // banner when the customer rec is incomplete.
        const cleanVehicleContext = vehicleContext?.make ? Object
          .entries({
            make:      vehicleContext.make,
            model:     vehicleContext.model,
            fuel_type: vehicleContext.fuel_type,
            year:      vehicleContext.year,
            mileage:   vehicleContext.mileage,
          })
          .filter(([, v]) => {
            if (v === null || v === undefined) return false;
            if (typeof v === "string" && v.trim() === "") return false;
            return true;
          })
          .reduce((acc, [k, v]) => ({ ...acc, [k]: v }), {})
          : null;
        const response = await axios.post(`${API}/jce-v2/analyse`, {
          repair_intent_codes: repairIntents,
          vehicle_context: cleanVehicleContext && Object.keys(cleanVehicleContext).length
            ? cleanVehicleContext
            : null,
        });
        setJceData(response.data);
        lastFetchParamsRef.current = recheckParamsKey;
      } catch (err) {
        console.error('JCE V2 analysis error:', err);
        setError('Failed to load JCE guidance');
      } finally {
        setLoading(false);
      }
    }, 500); // 500ms debounce

    return () => {
      if (jceFetchTimeoutRef.current) {
        clearTimeout(jceFetchTimeoutRef.current);
      }
    };
  }, [repairIntents, vehicleContext]);

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const handleAddSuggestion = (suggestion, quantity = 1) => {
    if (onAddSuggestion) {
      // Track this suggestion's code so it's filtered from the panel immediately
      if (suggestion.code) {
        setAddedSuggestionCodes(prev => new Set([...prev, suggestion.code.toUpperCase()]));
      }
      // Pass the quantity to the parent
      onAddSuggestion({ ...suggestion, quantity });
      const qtyText = quantity > 1 ? ` (x${quantity})` : '';
      toast.success(`Added: ${suggestion.label}${qtyText}`);
    }
  };
  
  // Track which suggestion is showing quantity selector
  const [showQuantityFor, setShowQuantityFor] = useState(null);

  if (!isOpen) return null;

  return (
    <div className="h-full flex flex-col bg-slate-900 text-white overflow-hidden" data-testid="jce-v2-panel">
      {/* Header - Compact */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-slate-700">
        <div className="flex items-center gap-2">
          <Zap className="h-4 w-4 text-amber-400" />
          <div>
            <h2 className="font-semibold text-sm">Job Intelligence</h2>
            {selectedJobTitle && (
              <p className="text-xs text-slate-400 truncate max-w-[180px]" title={selectedJobTitle}>
                {selectedJobTitle}
              </p>
            )}
          </div>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={onClose}
          className="text-slate-400 hover:text-white h-6 w-6 p-0"
        >
          <X className="h-3 w-3" />
        </Button>
      </div>

      {/* Content - Compact padding */}
      <div className="flex-1 overflow-y-auto p-3 space-y-3">
        {/* Technician Notes - Battery specific info */}
        {selectedJob && (selectedJob.battery_type_required || selectedJob.battery_registration_required || selectedJob.battery_technician_note || selectedJob.technician_notes) && (
          <div className="bg-orange-900/30 rounded-lg p-2 border border-orange-600">
            <div className="flex items-center gap-2 mb-2">
              <MessageSquare className="h-3 w-3 text-orange-400" />
              <span className="text-xs font-medium text-orange-300">Technician Notes</span>
            </div>
            
            {/* Battery Type */}
            {selectedJob.battery_type_required && (
              <div className="mb-2">
                <span className="text-[10px] text-orange-400 uppercase">Battery Type Required</span>
                <p className="text-sm text-white font-medium">
                  {selectedJob.battery_type_required === 'standard' ? 'Standard Lead-Acid' :
                   selectedJob.battery_type_required === 'efb' ? 'EFB (Enhanced Flooded Battery)' :
                   selectedJob.battery_type_required === 'agm' ? 'AGM (Absorbent Glass Mat)' :
                   'Unknown - Check Spec'}
                </p>
              </div>
            )}
            
            {/* Coding Required */}
            {selectedJob.battery_registration_required && (
              <div className="mb-2">
                <span className="text-[10px] text-orange-400 uppercase">Coding/Registration</span>
                <p className={`text-sm font-medium ${selectedJob.battery_registration_required === 'yes' ? 'text-red-300' : 'text-green-300'}`}>
                  {selectedJob.battery_registration_required === 'yes' ? '⚠️ Yes - Diagnostic Required' : '✓ No - Simple Swap'}
                </p>
              </div>
            )}
            
            {/* Technician Note */}
            {(selectedJob.battery_technician_note || selectedJob.technician_notes) && (
              <div>
                <span className="text-[10px] text-orange-400 uppercase">Tech Note</span>
                <p className="text-xs text-slate-300 italic">
                  "{selectedJob.battery_technician_note || selectedJob.technician_notes}"
                </p>
              </div>
            )}
          </div>
        )}

        {/* Summary - Compact */}
        {jceData && (
          <div className="bg-slate-800 rounded-lg p-2 border border-slate-700">
            <div className="flex items-center gap-2 mb-1">
              <Info className="h-3 w-3 text-blue-400" />
              <span className="text-xs font-medium text-slate-300">Summary</span>
            </div>
            <p className="text-xs text-slate-400">{jceData.summary}</p>
            <div className="flex gap-1 mt-1 flex-wrap">
              {repairIntents.map(intent => (
                <Badge key={intent} className="bg-blue-900/50 text-blue-300 text-[10px] px-1.5 py-0">
                  {intent.replace(/_/g, ' ')}
                </Badge>
              ))}
            </div>
          </div>
        )}

        {loading && (
          <div className="flex items-center justify-center py-8">
            <RefreshCw className="h-6 w-6 text-slate-400 animate-spin" />
          </div>
        )}

        {error && (
          <div className="bg-red-900/30 border border-red-700 rounded-lg p-3 text-red-300 text-sm">
            {error}
          </div>
        )}

        {!loading && !error && !selectedJob && (
          <div className="text-center py-8 text-slate-500">
            <Lightbulb className="h-8 w-8 mx-auto mb-2 opacity-50" />
            <p className="text-sm font-medium">Select a job to view intelligence</p>
            <p className="text-xs mt-1 text-slate-600">Click on a job card to see relevant suggestions</p>
          </div>
        )}
        
        {!loading && !error && selectedJob && repairIntents.length === 0 && !isServiceIntervalItem && (
          <div className="text-center py-8 text-slate-500">
            <Lightbulb className="h-8 w-8 mx-auto mb-2 opacity-50" />
            <p className="text-sm">No specific guidance for this job type</p>
          </div>
        )}

        {/* Service Mapping Content for Service Interval Items */}
        {!serviceMappingLoading && serviceMapping && isServiceIntervalItem && (
          <>
            {/* Service Mapping Summary */}
            <div className="bg-emerald-900/30 rounded-lg p-2 border border-emerald-700">
              <div className="flex items-center gap-2 mb-1">
                <Wrench className="h-3 w-3 text-emerald-400" />
                <span className="text-xs font-medium text-slate-300">Service Item</span>
                {serviceMapping.mapping_type === 'generic' && (
                  <Badge className="bg-slate-700 text-slate-300 text-[10px] px-1.5 py-0">
                    Generic Default
                  </Badge>
                )}
              </div>
              <p className="text-sm text-emerald-300 font-medium">{serviceMapping.label}</p>
              {serviceMapping.description && (
                <p className="text-xs text-slate-400 mt-1">{serviceMapping.description}</p>
              )}
            </div>

            {/* Advisor Guide */}
            {serviceMapping.job_guide && serviceMapping.job_guide.length > 0 && (
              <div className="bg-slate-800 rounded-lg border border-slate-700 overflow-hidden">
                <button
                  onClick={() => toggleSection('fohGuidance')}
                  className="w-full flex items-center justify-between p-3 hover:bg-slate-700/50 transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <FileText className="h-4 w-4 text-blue-400" />
                    <span className="font-medium text-sm">Advisor Guide</span>
                    <Badge className="bg-blue-900/50 text-blue-300 text-xs">
                      {serviceMapping.job_guide.length}
                    </Badge>
                  </div>
                  {expandedSections.fohGuidance ? (
                    <ChevronDown className="h-4 w-4 text-slate-400" />
                  ) : (
                    <ChevronRight className="h-4 w-4 text-slate-400" />
                  )}
                </button>
                {expandedSections.fohGuidance && (
                  <div className="px-3 pb-3 space-y-2">
                    {serviceMapping.job_guide.sort((a, b) => a.order - b.order).map((step, idx) => (
                      <div 
                        key={idx}
                        className={`flex items-start gap-2 p-2 rounded text-xs ${
                          step.is_warning 
                            ? 'bg-amber-900/30 border border-amber-700' 
                            : 'bg-slate-700/50'
                        }`}
                      >
                        <span className={`flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold ${
                          step.is_warning ? 'bg-amber-600 text-white' : 'bg-emerald-600 text-white'
                        }`}>
                          {step.is_warning ? '!' : idx + 1}
                        </span>
                        <span className={step.is_warning ? 'text-amber-300' : 'text-slate-300'}>
                          {step.text}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Suggested Parts */}
            {serviceMapping.suggested_parts && serviceMapping.suggested_parts.length > 0 && (
              <div className="bg-slate-800 rounded-lg border border-slate-700 overflow-hidden">
                <button
                  onClick={() => toggleSection('suggestions')}
                  className="w-full flex items-center justify-between p-3 hover:bg-slate-700/50 transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <Lightbulb className="h-4 w-4 text-emerald-400" />
                    <span className="font-medium text-sm">Suggested Parts</span>
                    <Badge className="bg-emerald-900/50 text-emerald-300 text-xs">
                      {serviceMapping.suggested_parts.length}
                    </Badge>
                  </div>
                  {expandedSections.suggestions ? (
                    <ChevronDown className="h-4 w-4 text-slate-400" />
                  ) : (
                    <ChevronRight className="h-4 w-4 text-slate-400" />
                  )}
                </button>
                {expandedSections.suggestions && (
                  <div className="px-3 pb-3 space-y-1.5">
                    {serviceMapping.suggested_parts.map((part, idx) => (
                      <div key={idx} className="flex items-center justify-between bg-slate-700/50 rounded p-2">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="text-sm text-white">{part.name}</span>
                            {part.is_required && (
                              <Badge className="bg-blue-600 text-[10px] px-1 py-0">Required</Badge>
                            )}
                          </div>
                          {part.description && (
                            <p className="text-xs text-slate-400 mt-0.5">{part.description}</p>
                          )}
                        </div>
                        <Button
                          size="sm"
                          className="h-6 text-xs bg-emerald-600 hover:bg-emerald-700"
                          onClick={() => handleAddSuggestion({
                            type: 'part',
                            label: part.name,
                            description: part.description,
                            quantity: part.quantity || 1
                          })}
                        >
                          <Plus className="h-3 w-3 mr-1" /> Add
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Suggested Labour */}
            {serviceMapping.suggested_labour && serviceMapping.suggested_labour.length > 0 && (
              <div className="bg-slate-800 rounded-lg border border-slate-700 overflow-hidden">
                <div className="p-3">
                  <div className="flex items-center gap-2 mb-2">
                    <Wrench className="h-4 w-4 text-purple-400" />
                    <span className="font-medium text-sm">Suggested Labour</span>
                  </div>
                  <div className="space-y-1.5">
                    {serviceMapping.suggested_labour.map((labour, idx) => (
                      <div key={idx} className="flex items-center justify-between bg-slate-700/50 rounded p-2">
                        <div className="flex-1">
                          <span className="text-sm text-white">{labour.description}</span>
                          {labour.hours && (
                            <p className="text-xs text-slate-400">{labour.hours} hours</p>
                          )}
                        </div>
                        <Button
                          size="sm"
                          className="h-6 text-xs bg-purple-600 hover:bg-purple-700"
                          onClick={() => handleAddSuggestion({
                            type: 'labour',
                            label: labour.description,
                            hours: labour.hours
                          })}
                        >
                          <Plus className="h-3 w-3 mr-1" /> Add
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        {jceData && (
          <>
            {/* Disturbances Section */}
            {jceData.disturbances.length > 0 && (
              <div className="bg-slate-800 rounded-lg border border-slate-700 overflow-hidden">
                <button
                  onClick={() => toggleSection('disturbances')}
                  className="w-full flex items-center justify-between p-3 hover:bg-slate-700/50 transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <Link2 className="h-4 w-4 text-orange-400" />
                    <span className="font-medium text-sm">Components Disturbed</span>
                    <Badge className="bg-orange-900/50 text-orange-300 text-xs">
                      {jceData.disturbances.length}
                    </Badge>
                  </div>
                  {expandedSections.disturbances ? (
                    <ChevronDown className="h-4 w-4 text-slate-400" />
                  ) : (
                    <ChevronRight className="h-4 w-4 text-slate-400" />
                  )}
                </button>
                {expandedSections.disturbances && (
                  <div className="px-3 pb-3 space-y-2">
                    {jceData.disturbances.map((d, idx) => (
                      <div key={idx} className="flex items-start justify-between gap-2 bg-slate-900/50 rounded p-2">
                        <div className="flex-1">
                          <p className="text-sm text-slate-300">{d.label}</p>
                          <p className="text-xs text-slate-500">{d.source_rule_name}</p>
                        </div>
                        {getConfidenceBadge(d.confidence)}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Suggested Additions Section - Use filteredJceData to hide already-added items */}
            {filteredJceData?.suggested_additions?.length > 0 && (
              <div className="bg-slate-800 rounded-lg border border-slate-700 overflow-hidden">
                <button
                  onClick={() => toggleSection('suggestions')}
                  className="w-full flex items-center justify-between p-3 hover:bg-slate-700/50 transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <Plus className="h-4 w-4 text-green-400" />
                    <span className="font-medium text-sm">Consider Adding</span>
                    <Badge className="bg-green-900/50 text-green-300 text-xs">
                      {filteredJceData.suggested_additions.length}
                    </Badge>
                  </div>
                  {expandedSections.suggestions ? (
                    <ChevronDown className="h-4 w-4 text-slate-400" />
                  ) : (
                    <ChevronRight className="h-4 w-4 text-slate-400" />
                  )}
                </button>
                {expandedSections.suggestions && (
                  <div className="px-3 pb-3 space-y-2">
                    {filteredJceData.suggested_additions.map((s, idx) => {
                      // Check if this is a paired item (typically done in pairs)
                      const isPairedItem = s.label?.toLowerCase().includes('mount') ||
                                          s.label?.toLowerCase().includes('shock') ||
                                          s.label?.toLowerCase().includes('strut') ||
                                          s.label?.toLowerCase().includes('spring') ||
                                          s.label?.toLowerCase().includes('ball joint') ||
                                          s.label?.toLowerCase().includes('track rod') ||
                                          s.label?.toLowerCase().includes('drop link') ||
                                          s.label?.toLowerCase().includes('cv boot');
                      
                      return (
                        <div key={idx} className="bg-slate-900/50 rounded p-2">
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <span className="text-sm font-medium text-slate-200">{s.label}</span>
                                {s.auto_add && (
                                  <Badge className="bg-red-900/50 text-red-300 text-xs">Required</Badge>
                                )}
                                {getConfidenceBadge(s.confidence)}
                              </div>
                              {s.reason && (
                                <p className="text-xs text-slate-400 mt-1">{s.reason}</p>
                              )}
                              <div className="flex gap-3 mt-1 text-xs text-slate-500">
                                {s.estimated_labour_hours && (
                                  <span>~{s.estimated_labour_hours}h labour</span>
                                )}
                                {s.estimated_part_cost && (
                                  <span>~£{s.estimated_part_cost.toFixed(2)} parts</span>
                                )}
                              </div>
                            </div>
                            
                            {/* Quantity selector for paired items */}
                            {isPairedItem ? (
                              <div className="flex items-center gap-1">
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="h-7 px-2 text-green-400 hover:text-green-300 hover:bg-green-900/30 text-xs"
                                  onClick={() => handleAddSuggestion(s, 1)}
                                  data-testid={`add-suggestion-${s.code}-single`}
                                  title="Add 1 (single side)"
                                >
                                  +1
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="h-7 px-2 text-blue-400 hover:text-blue-300 hover:bg-blue-900/30 text-xs font-semibold"
                                  onClick={() => handleAddSuggestion(s, 2)}
                                  data-testid={`add-suggestion-${s.code}-both`}
                                  title="Add 2 (both sides)"
                                >
                                  +2
                                </Button>
                              </div>
                            ) : (
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-7 px-2 text-green-400 hover:text-green-300 hover:bg-green-900/30"
                                onClick={() => handleAddSuggestion(s, 1)}
                                data-testid={`add-suggestion-${s.code}`}
                              >
                                <Plus className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* Risks Section */}
            {jceData.risks.length > 0 && (
              <div className="bg-slate-800 rounded-lg border border-slate-700 overflow-hidden">
                <button
                  onClick={() => toggleSection('risks')}
                  className="w-full flex items-center justify-between p-3 hover:bg-slate-700/50 transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <AlertTriangle className="h-4 w-4 text-amber-400" />
                    <span className="font-medium text-sm">Repair Considerations</span>
                    <Badge className="bg-amber-900/50 text-amber-300 text-xs">
                      {jceData.risks.length}
                    </Badge>
                  </div>
                  {expandedSections.risks ? (
                    <ChevronDown className="h-4 w-4 text-slate-400" />
                  ) : (
                    <ChevronRight className="h-4 w-4 text-slate-400" />
                  )}
                </button>
                {expandedSections.risks && (
                  <div className="px-3 pb-3 space-y-2">
                    {jceData.risks.map((r, idx) => (
                      <div key={idx} className="bg-slate-900/50 rounded p-2">
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1">
                            <p className="text-sm text-slate-300">{r.label}</p>
                            {r.mitigation && (
                              <p className="text-xs text-slate-400 mt-1 italic">→ {r.mitigation}</p>
                            )}
                          </div>
                          {getConfidenceBadge(r.confidence)}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* FOH Pricing Guidance Section */}
            {jceData.foh_pricing_guidance.length > 0 && (
              <div className="bg-slate-800 rounded-lg border border-slate-700 overflow-hidden">
                <button
                  onClick={() => toggleSection('fohGuidance')}
                  className="w-full flex items-center justify-between p-3 hover:bg-slate-700/50 transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <Lightbulb className="h-4 w-4 text-blue-400" />
                    <span className="font-medium text-sm">Pricing Tips</span>
                  </div>
                  {expandedSections.fohGuidance ? (
                    <ChevronDown className="h-4 w-4 text-slate-400" />
                  ) : (
                    <ChevronRight className="h-4 w-4 text-slate-400" />
                  )}
                </button>
                {expandedSections.fohGuidance && (
                  <div className="px-3 pb-3">
                    <ul className="space-y-1">
                      {jceData.foh_pricing_guidance.map((tip, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-sm text-slate-300">
                          <Check className="h-4 w-4 text-blue-400 mt-0.5 flex-shrink-0" />
                          {tip}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}

            {/* Customer Explanation Section */}
            {jceData.customer_explanation_guidance.length > 0 && (
              <div className="bg-slate-800 rounded-lg border border-slate-700 overflow-hidden">
                <button
                  onClick={() => toggleSection('customerExplanation')}
                  className="w-full flex items-center justify-between p-3 hover:bg-slate-700/50 transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <MessageSquare className="h-4 w-4 text-purple-400" />
                    <span className="font-medium text-sm">Customer Explanation</span>
                  </div>
                  {expandedSections.customerExplanation ? (
                    <ChevronDown className="h-4 w-4 text-slate-400" />
                  ) : (
                    <ChevronRight className="h-4 w-4 text-slate-400" />
                  )}
                </button>
                {expandedSections.customerExplanation && (
                  <div className="px-3 pb-3">
                    <ul className="space-y-2">
                      {jceData.customer_explanation_guidance.map((text, idx) => (
                        <li key={idx} className="text-sm text-slate-300 bg-purple-900/20 rounded p-2 border border-purple-800/30">
                          "{text}"
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* Footer */}
      <div className="p-3 border-t border-slate-700 bg-slate-800/50">
        <p className="text-xs text-slate-500 text-center">
          {jceData?.rules_matched || 0} rules matched • Deterministic guidance
        </p>
      </div>
    </div>
  );
};

export default JCEV2Panel;
